/**
 * GCLID Tracking for Shopify
 * 捕获 Google Click ID 并通过 Cart Attributes 传递到订单
 * 
 * 工作流程：
 * 1. 页面加载时从 URL 读取 gclid 参数
 * 2. 将 gclid 存入 Cookie（有效期 30 天）
 * 3. 每次页面加载/加购时，确保 Cart Attributes 包含 GCLID
 * 4. 结账时 gclid 会随订单一起保存
 * 5. Shopify Flow 可监听订单创建事件，读取 gclid 做后续处理
 */

(function () {
    'use strict';

    // 配置
    const CONFIG = {
        COOKIE_NAME: '_garvee_gclid',
        COOKIE_DAYS: 30,  // 延长至 30 天，覆盖更长决策周期
        ATTRIBUTE_KEY: 'gclid',
        // 需要追踪的 URL 参数
        TRACKED_PARAMS: ['gclid', 'wbraid', 'gbraid'],
        // 是否允许覆盖已有的 GCLID（false = 首次点击归因，true = 末次点击归因）
        ALLOW_OVERWRITE: true,
        // 重试配置
        RETRY_DELAY: 1000,
        MAX_RETRIES: 3,
    };

    /**
     * 设置 Cookie
     */
    function setCookie(name, value, days) {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        const secureFlag = window.location.protocol === 'https:' ? ';Secure' : '';
        document.cookie = name + '=' + encodeURIComponent(value) + ';expires=' + expires.toUTCString() + ';path=/;domain=.garvee.es;SameSite=Lax' + secureFlag;
    }

    /**
     * 获取 Cookie
     */
    function getCookie(name) {
        const nameEQ = name + '=';
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i].trim();
            if (c.indexOf(nameEQ) === 0) {
                return decodeURIComponent(c.substring(nameEQ.length));
            }
        }
        return null;
    }

    /**
     * 从 URL 获取参数值
     */
    function getUrlParam(name) {
        const params = new URLSearchParams(window.location.search);
        return params.get(name);
    }

    /**
     * 捕获并存储追踪参数
     */
    function captureTrackingParams() {
        const existingValue = getCookie(CONFIG.COOKIE_NAME);

        for (const param of CONFIG.TRACKED_PARAMS) {
            const urlValue = getUrlParam(param);
            if (urlValue) {
                if (CONFIG.ALLOW_OVERWRITE || !existingValue) {
                    // 允许覆盖 或 首次捕获 → 更新 Cookie
                    setCookie(CONFIG.COOKIE_NAME, urlValue, CONFIG.COOKIE_DAYS);
                    console.log('[GCLID Tracker] Captured ' + param + ' from URL:', urlValue);
                    return urlValue;
                } else {
                    // 不允许覆盖 → 保留首次捕获的值
                    console.log('[GCLID Tracker] Keeping first-click value:', existingValue);
                    return existingValue;
                }
            }
        }
        // 从 Cookie 读取已有的值
        if (existingValue) {
            console.log('[GCLID Tracker] Retrieved from cookie:', existingValue);
            return existingValue;
        }
        return null;
    }

    /**
     * 更新 Cart Attributes（带重试机制）
     */
    function updateCartAttributes(attributes, retryCount = 0) {
        return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: attributes }),
        })
            .then((response) => {
                if (!response.ok) throw new Error('HTTP ' + response.status);
                return response.json();
            })
            .then((data) => {
                console.log('[GCLID Tracker] Cart attributes updated:', data);
                return data;
            })
            .catch((error) => {
                console.error('[GCLID Tracker] Failed to update cart attributes:', error);
                if (retryCount < CONFIG.MAX_RETRIES) {
                    console.log('[GCLID Tracker] Retrying... (' + (retryCount + 1) + '/' + CONFIG.MAX_RETRIES + ')');
                    return new Promise((resolve) => {
                        setTimeout(() => {
                            resolve(updateCartAttributes(attributes, retryCount + 1));
                        }, CONFIG.RETRY_DELAY * (retryCount + 1));
                    });
                }
                return null;
            });
    }

    /**
     * 确保购物车包含 GCLID（核心函数）
     * 策略：页面加载时立即写入；若购物车尚未初始化，返回 false 由调用方延迟重试
     * @returns {Promise<boolean>} true=成功写入，false=购物车不存在需要延迟重试
     */
    function ensureGclidInCart() {
        const trackingValue = captureTrackingParams();
        if (!trackingValue) {
            console.log('[GCLID Tracker] No tracking parameter found');
            return Promise.resolve(true);
        }

        return fetch('/cart.js')
            .then((response) => {
                if (!response.ok) throw new Error('HTTP ' + response.status);
                return response.json();
            })
            .then((cart) => {
                // 购物车尚未初始化（不含 item_count 字段），推迟到首次加购后再写
                if (cart.item_count === undefined) {
                    console.log('[GCLID Tracker] Cart not initialized, deferring to first add-to-cart');
                    return false;
                }

                // 检查是否已存在且值相同
                if (cart.attributes && cart.attributes[CONFIG.ATTRIBUTE_KEY] === trackingValue) {
                    console.log('[GCLID Tracker] GCLID already in cart attributes');
                    return true;
                }

                // 合并现有 attributes，避免覆盖其他自定义属性
                const attributes = Object.assign({}, cart.attributes || {});
                attributes[CONFIG.ATTRIBUTE_KEY] = trackingValue;

                console.log('[GCLID Tracker] Writing GCLID to cart:', trackingValue, '(cart items:', cart.item_count, ')');
                return updateCartAttributes(attributes).then(function () { return true; });
            })
            .catch((error) => {
                console.error('[GCLID Tracker] Failed to fetch cart:', error);
                // 网络错误也视为需要重试
                return false;
            });
    }

    /**
     * 初始化
     * 策略：
     * 1. 页面加载时尝试写入 GCLID 到 Cart Attributes
     * 2. 若购物车尚未初始化（item_count === undefined），订阅 cartUpdate 事件
     * 3. 首次加购触发 cartUpdate → 此时购物车一定存在 → 写入成功
     */
    function init() {
        console.log('[GCLID Tracker] Initializing...');

        // 1. 捕获并存储追踪参数到 Cookie
        captureTrackingParams();

        // 2. 尝试写入 Cart Attributes，若购物车不存在则订阅事件等待首次加购
        ensureGclidInCart().then(function (success) {
            if (!success) {
                console.log('[GCLID Tracker] Deferred — waiting for first cart update event');

                var cartEventUnsubscribe = MinimogEvents.subscribe(
                    MinimogTheme.pubSubEvents.cartUpdate,
                    function (data) {
                        // 确认购物车已有商品（item_count > 0），此时购物车一定已初始化
                        if (data && data.item_count > 0) {
                            console.log('[GCLID Tracker] Cart update detected, retrying GCLID write...');
                            ensureGclidInCart().then(function (retrySuccess) {
                                if (retrySuccess) {
                                    console.log('[GCLID Tracker] Deferred GCLID write succeeded');
                                }
                            });
                            cartEventUnsubscribe(); // 只执行一次
                        }
                    }
                );

                // 安全兜底：若 30 秒内仍未触发加购，尝试再次写入（此时购物车可能已被其他组件初始化）
                setTimeout(function () {
                    console.log('[GCLID Tracker] Timeout — retrying GCLID write after delay');
                    ensureGclidInCart().then(function (retrySuccess) {
                        if (retrySuccess) {
                            cartEventUnsubscribe();
                        }
                    });
                }, 30000);
            }
        });

        // 3. 暴露全局方法
        window.garveeGclidTracker = {
            getGclid: captureTrackingParams,
            updateCart: ensureGclidInCart,
        };
    }

    // 页面加载完成后初始化
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
